<template>
  <transition name="ip-slide">
    <div v-if="visible" class="ip-wrap" :class="{ 'ip-above-nav': hasBottomNav }">
      <div class="ip-card">
        <!-- Header row -->
        <div class="ip-head">
          <div class="ip-icon">
            <img :src="iconSrc" alt="" />
          </div>
          <div class="ip-text">
            <p class="ip-title">{{ $t('install.title') }}</p>
            <p class="ip-sub">{{ $t('install.subtitle') }}</p>
          </div>
          <button class="ip-close" :aria-label="$t('install.later')" @click="dismiss">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <!-- Android / Chrome: real install button -->
        <div v-if="canPrompt" class="ip-actions">
          <button class="ip-btn ip-btn--primary" @click="install">
            <v-icon size="18" color="#fff" class="mr-1">mdi-download</v-icon>
            {{ $t('install.action') }}
          </button>
          <button class="ip-btn ip-btn--ghost" @click="dismiss">
            {{ $t('install.later') }}
          </button>
        </div>

        <!-- iOS: instructions only (Apple allows no prompt API) -->
        <div v-else-if="isIos" class="ip-steps">
          <div class="ip-step">
            <span class="ip-step-num">1</span>
            <span>{{ $t('install.ios_step1') }}</span>
            <v-icon size="17" class="ip-share">mdi-export-variant</v-icon>
          </div>
          <div class="ip-step">
            <span class="ip-step-num">2</span>
            <span>{{ $t('install.ios_step2') }}</span>
            <v-icon size="17" class="ip-share">mdi-plus-box-outline</v-icon>
          </div>
          <!-- iOS i jep aplikacionit te shtuar nje hapesire ruajtjeje te veten,
               pra kycja e bere ne shfletues nuk kalon aty. Thuhet para se te
               ndodhe, perndryshe duket si defekt. -->
          <div class="ip-step">
            <span class="ip-step-num">3</span>
            <span>{{ $t('install.ios_step3') }}</span>
          </div>
          <button class="ip-btn ip-btn--ghost ip-btn--full" @click="dismiss">
            {{ $t('install.later') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const DISMISS_KEY = 'pwaInstallDismissed';

export default {
  name: 'InstallPrompt',
  data() {
    return {
      visible: false,
      deferredPrompt: null,
      canPrompt: false,
      isIos: false,
    };
  },
  computed: {
    hasBottomNav() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    iconSrc() {
      return '/img/icons/apple-touch-icon.png';
    },
  },
  created() {
    // Already running as an installed app — nothing to offer.
    if (this.isStandalone()) return;

    // Dismissed earlier in this session; show again on the next visit.
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    // Only worth offering on phones/tablets.
    if (!this.$vuetify.breakpoint.smAndDown) return;

    this.isIos = this.detectIos();

    window.addEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.addEventListener('appinstalled', this.onInstalled);

    // Chrome fires beforeinstallprompt shortly after load; iOS never does,
    // so surface the manual instructions after a short delay instead.
    if (this.isIos) {
      setTimeout(() => {
        if (!this.isStandalone()) this.visible = true;
      }, 2500);
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstall);
    window.removeEventListener('appinstalled', this.onInstalled);
  },
  methods: {
    isStandalone() {
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
      );
    },
    detectIos() {
      const ua = window.navigator.userAgent;
      const iDevice = /iphone|ipad|ipod/i.test(ua);
      // iPadOS 13+ reports as Mac; distinguish by touch support.
      const iPadDesktopUa = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
      return iDevice || iPadDesktopUa;
    },
    onBeforeInstall(e) {
      e.preventDefault();
      this.deferredPrompt = e;
      this.canPrompt = true;
      this.visible = true;
    },
    onInstalled() {
      this.visible = false;
      this.deferredPrompt = null;
    },
    async install() {
      if (!this.deferredPrompt) return;
      this.deferredPrompt.prompt();
      try {
        await this.deferredPrompt.userChoice;
      } catch (e) {
        // User dismissed the native dialog — nothing to handle.
      }
      this.deferredPrompt = null;
      this.canPrompt = false;
      this.visible = false;
    },
    dismiss() {
      sessionStorage.setItem(DISMISS_KEY, '1');
      this.visible = false;
    },
  },
};
</script>

<style scoped>
.ip-wrap {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  z-index: 250;
  pointer-events: none;
}
.ip-above-nav {
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
}
.ip-card {
  pointer-events: auto;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 14px 14px 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  max-width: 520px;
  margin: 0 auto;
}
.ip-head {
  display: flex;
  align-items: flex-start;
  gap: 11px;
}
.ip-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(230, 81, 0, 0.08);
}
.ip-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.ip-text {
  flex: 1;
  min-width: 0;
}
.ip-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a2744;
}
.ip-sub {
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;
}
.ip-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(0, 0, 0, 0.05);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ip-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

.ip-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.ip-btn {
  height: 38px;
  border-radius: 10px;
  border: none;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  transition: filter 0.15s, background 0.15s;
}
.ip-btn--primary {
  flex: 1;
  background: linear-gradient(90deg, #e8701e, #f5a030);
  color: #fff;
}
.ip-btn--primary:hover {
  filter: brightness(1.05);
}
.ip-btn--ghost {
  background: rgba(0, 0, 0, 0.05);
  color: #475569;
}
.ip-btn--ghost:hover {
  background: rgba(0, 0, 0, 0.1);
}
.ip-btn--full {
  width: 100%;
  margin-top: 10px;
}

.ip-steps {
  margin-top: 11px;
}
.ip-step {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #334155;
  padding: 6px 0;
}
.ip-step-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(230, 81, 0, 0.12);
  color: #e65100;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ip-share {
  color: #e65100 !important;
}

/* Dark theme */
.theme--dark .ip-card {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.1);
}
.theme--dark .ip-title {
  color: #f1f5f9;
}
.theme--dark .ip-sub {
  color: #94a3b8;
}
.theme--dark .ip-close {
  background: rgba(255, 255, 255, 0.08);
}
.theme--dark .ip-close:hover {
  background: rgba(255, 255, 255, 0.16);
}
.theme--dark .ip-btn--ghost {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}
.theme--dark .ip-step {
  color: #cbd5e1;
}

.ip-slide-enter-active,
.ip-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.ip-slide-enter,
.ip-slide-leave-to {
  transform: translateY(120%);
  opacity: 0;
}
</style>
